import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocalize } from '@/i18n/localize'
import { COMPUTE_BLADES } from '@/services/blades/bladeRegistry'
import {
  bladeCanonical,
  bladeSeoTitle,
  bladeSeoDescription,
  bladeJsonLd,
} from '@/services/blades/bladeSeo'
import { useSeo } from '@/lib/useSeo'
import { trackEvent } from '@/lib/posthog'

/**
 * RouteSeo — sets per-page <title>/description/canonical for the compute-blade
 * tool pages (`/tools/*`) from the registry, so each tool ranks on its own
 * term without editing the agent-built pages individually. It also emits
 * per-tool Open Graph / Twitter tags (so a shared tool link previews as the
 * tool, not the generic homepage) and JSON-LD WebApplication structured data
 * (free-tool rich results).
 *
 * Mounted once inside the Router. The hub (`/tools`) and the templated door
 * pages (`/d/:id`) manage their own SEO via useSeo, so this only fires on a
 * matched compute-blade tool route; otherwise it no-ops (empty title).
 */
const OG_IMAGE = 'https://beetlesense.ai/og-image.png'

export function RouteSeo() {
  const { pathname } = useLocation()
  const { isSv } = useLocalize()

  const blade = COMPUTE_BLADES.find((b) => b.route === pathname)

  // Per-tool usage tracking — one event per tool view, tagged with the host so
  // each app/subdomain's traffic is sliceable in PostHog (complements the
  // Vercel Web Analytics path tracking already mounted in index.tsx).
  useEffect(() => {
    if (!blade) return
    trackEvent('tool_view', {
      tool: blade.id,
      surface: 'compute',
      host: typeof window !== 'undefined' ? window.location.hostname : undefined,
    })
  }, [blade])

  // Title / description / canonical (existing mechanism).
  useSeo(
    blade
      ? {
          title: bladeSeoTitle(blade, isSv),
          description: bladeSeoDescription(blade, isSv),
          canonical: bladeCanonical(blade),
        }
      : { title: '' },
  )

  // Open Graph / Twitter + JSON-LD — injected here (not in the shared useSeo)
  // so the extra tags are scoped to tool pages and fully cleaned up on leave.
  useEffect(() => {
    if (!blade || typeof document === 'undefined') return

    const title = bladeSeoTitle(blade, isSv)
    const description = bladeSeoDescription(blade, isSv)
    const url = bladeCanonical(blade)

    const meta: { key: 'property' | 'name'; val: string; content: string }[] = [
      { key: 'property', val: 'og:title', content: title },
      { key: 'property', val: 'og:description', content: description },
      { key: 'property', val: 'og:url', content: url },
      { key: 'property', val: 'og:type', content: 'website' },
      { key: 'property', val: 'og:image', content: OG_IMAGE },
      { key: 'property', val: 'og:site_name', content: 'BeetleSense' },
      { key: 'name', val: 'twitter:card', content: 'summary_large_image' },
      { key: 'name', val: 'twitter:title', content: title },
      { key: 'name', val: 'twitter:description', content: description },
      { key: 'name', val: 'twitter:image', content: OG_IMAGE },
    ]

    // Upsert each tag, remembering whether we created it and its prior value so
    // the page is restored to its pre-tool state on unmount/navigation.
    const restorers = meta.map(({ key, val, content }) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${val}"]`)
      const created = !el
      const prev = el?.getAttribute('content') ?? null
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(key, val)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
      return () => {
        if (created) el!.remove()
        else if (prev !== null) el!.setAttribute('content', prev)
      }
    })

    // JSON-LD structured data block (scoped, removed on cleanup).
    const ld = document.createElement('script')
    ld.type = 'application/ld+json'
    ld.setAttribute('data-blade-seo', blade.id)
    ld.textContent = JSON.stringify(bladeJsonLd(blade, isSv))
    document.head.appendChild(ld)

    return () => {
      restorers.forEach((restore) => restore())
      ld.remove()
    }
  }, [blade, isSv])

  return null
}

export default RouteSeo
